import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { UserNav } from './UserNav';
import { CreditsDisplay } from '@/components/CreditsDisplay';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { authService } from '@/services/auth.service';

export function Header() {
  const { user, loading, setUser } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check auth state when mounted and when route changes
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await authService.verifyToken(token);
          setUser(userData);
        } catch (error) {
          console.error('Header auth check failed:', error);
        }
      }
    };

    checkAuth();
  }, [location.pathname, setUser]); // Re-check when route changes

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white relative">
      <div className="container flex h-16 items-center justify-between">
        <Link
          to={user ? '/dashboard' : '/'}
          className="font-bold text-xl"
        >
          SmartReel
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/showcase">
            <Button variant="ghost">Gallery</Button>
          </Link>

          {/* Only show Pricing when not signed in */}
          {!user && (
            <Link to="/pricing">
              <Button variant="ghost">Pricing</Button>
            </Link>
          )}

          {/* Show credits for logged in users */}
          {user && (
            <Link to="/profile">
              <CreditsDisplay />
            </Link>
          )}

          {user ? (
            <UserNav user={user} />
          ) : (
            <Link to="/auth">
              <Button>Sign In</Button>
            </Link>
          )}
        </div>

        {/* Mobile nav: keep the primary action visible, tuck links behind a toggle */}
        <div className="flex md:hidden items-center gap-2">
          {user ? (
            <UserNav user={user} />
          ) : (
            <Link to="/auth">
              <Button size="sm">Sign In</Button>
            </Link>
          )}
          <button
            type="button"
            onClick={() => setMobileOpen(open => !open)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — full-width sheet under the header, overlay (no content shift) */}
      {mobileOpen && (
        <div className="md:hidden">
          {/* Tap-away backdrop (below the header) */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-x-0 bottom-0 top-16 z-40 cursor-default"
          />
          <div className="absolute inset-x-0 top-full z-50 border-b bg-white shadow-sm">
            <nav className="container flex flex-col py-2">
              <Link
                to="/showcase"
                className="rounded-md px-2 py-3 text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                Gallery
              </Link>
              {!user && (
                <Link
                  to="/pricing"
                  className="rounded-md px-2 py-3 text-base font-medium text-gray-700 hover:bg-gray-50"
                >
                  Pricing
                </Link>
              )}
              {user && (
                <Link to="/profile" className="px-2 py-3">
                  <CreditsDisplay />
                </Link>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
